import { FavoriteStar } from './FavoriteStar';
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { useWrite } from './MoneyForms';
import { useResource, refetchResource } from '../lib/store';
import { session } from '../lib/session';
import { LOCATION_ORDER, LOCATION_NAMES, type LocationCode } from '../lib/locations';
import {
  COMPANY_VENDORS_KEY, vendorProfileKey, fetchCompanyVendors, fetchCompanyVendor,
  saveCompanyVendor, removeCompanyVendor, addVendorLink, removeVendorFile, uploadVendorFile,
  promoteCompanyVendor, demoteCompanyVendor, saveVendorClinicNote,
  sortVendorRows, categoriesInUse,
  type CompanyVendor, type CompanyVendorFile,
} from '../lib/companyVendors';

const EM_DASH = '—';

function canWriteVendors(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR' || me.role === 'MANAGER'));
}

/**
 * N-096 (owner, 2026-09-10): moving a vendor between Company and a clinic's
 * own list changes what every clinic sees, so — the same split the server
 * enforces (src/company_vendors.js's canChangeVendorScope) — only an owner
 * or director may do it, never while viewing-as.
 */
function canManageVendorScope(): boolean {
  // Owner, 2026-09-10: "viewable only to me" — the owner alone moves vendors
  // between Company and a clinic.
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && me.role === 'OWNER');
}

/** Company-wide vendors are added, edited and deleted by the owner only
 * (owner, 2026-09-10); a clinic's own vendors keep canWriteVendors. */
function canWriteCompanyWide(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && me.role === 'OWNER');
}

const FIELDS = [
  ['name', 'Name'],
  ['category', 'Category'],
  ['contactName', 'Contact name'],
  ['phone', 'Phone'],
  ['email', 'Email'],
  ['website', 'Website'],
  ['notes', 'Notes'],
] as const;

/**
 * A COMPANY-scope vendor's shared fields never include notes — notes are
 * per clinic (clinicNotes) once a vendor is company-wide, edited through
 * ClinicNoteSection below rather than the shared-fields form. Category is a
 * normal shared field either way (N-097), unlike notes.
 */
function fieldsForScope(scope: 'COMPANY' | 'LOCATION'): typeof FIELDS[number][] {
  return scope === 'COMPANY' ? FIELDS.filter(([key]) => key !== 'notes') : [...FIELDS];
}

function blankForm(): Record<string, string> {
  return { name: '', category: '', contactName: '', phone: '', email: '', website: '', notes: '' };
}

function fromVendor(v: CompanyVendor): Record<string, string> {
  return {
    name: v.name ?? '',
    category: v.category ?? '',
    contactName: v.contactName ?? '',
    phone: v.phone ?? '',
    email: v.email ?? '',
    website: v.website ?? '',
    notes: v.notes ?? '',
  };
}

export function CompanyVendors(
  { clinics, vendorId, onOpen, onBack, pinKey }:
  {
    clinics: LocationCode[];
    vendorId: number | null;
    onOpen: (id: number) => void;
    onBack: () => void;
    pinKey?: string;
  },
): JSX.Element {
  const list = useResource<{ vendors: CompanyVendor[] }>(COMPANY_VENDORS_KEY, fetchCompanyVendors);
  // N-096: the clinic whose note a company-wide vendor shows — the clinic
  // currently selected on the Company page. `clinics` holds exactly one
  // entry there; kept as clinics[0] rather than requiring length 1 so this
  // still renders sensibly wherever more than one clinic is passed in.
  const myClinic = clinics[0] ?? null;
  // N-097: every category already in use, across every vendor this caller
  // can see (not just the one table being edited) — the add/edit form's
  // datalist. Computed here, once, so VendorCard (a separate branch below)
  // gets the same list without a second fetch — `list` is already read
  // unconditionally above.
  const categories = list.status === 'ready' ? categoriesInUse(list.data.vendors ?? []) : [];
  if (vendorId !== null) {
    return <VendorCard vendorId={vendorId} onBack={onBack} myClinic={myClinic} categories={categories} />;
  }
  if (list.status === 'loading') return <EmptyState label="Loading vendors…" />;
  if (list.status === 'fault') return <FaultState name="vendors_load_failed" detail={list.error} />;
  const vendors = list.data.vendors ?? [];
  return (
    <div data-testid="company-vendors">
      <VendorTable
        title="Company vendors"
        rows={vendors.filter((v) => v.scope === 'COMPANY')}
        scope="COMPANY"
        myClinic={myClinic}
        categories={categories}
        onOpen={onOpen}
      />
      {clinics.map((code) => (
        <VendorTable
          key={code}
          pin={pinKey ? <FavoriteStar itemKey={pinKey} /> : null}
          title={`${LOCATION_NAMES[code] ?? code} vendors`}
          rows={vendors.filter((v) => v.scope === 'LOCATION' && v.locationCode === code)}
          scope="LOCATION"
          locationCode={code}
          categories={categories}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}

function VendorTable(
  { title, rows, scope, locationCode, myClinic, categories, onOpen, pin }:
  {
    title: string;
    pin?: JSX.Element | null;
    rows: CompanyVendor[];
    scope: 'COMPANY' | 'LOCATION';
    locationCode?: string;
    myClinic?: LocationCode | null;
    categories: string[];
    onOpen: (id: number) => void;
  },
): JSX.Element {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(blankForm);
  const { busy, error, run } = useWrite();
  const canWrite = scope === 'COMPANY' ? canWriteCompanyWide() : canWriteVendors();
  const canManage = canManageVendorScope();
  const fields = fieldsForScope(scope);
  // N-097: rows sorted by category then name, blank category last.
  const sortedRows = sortVendorRows(rows);
  const categoryListId = `vendor-categories-${scope}-${locationCode ?? 'company'}`;

  async function save(): Promise<void> {
    await saveCompanyVendor({
      scope,
      locationCode: locationCode ?? null,
      ...form,
    });
    await refetchResource(COMPANY_VENDORS_KEY, fetchCompanyVendors);
    setForm(blankForm());
    setAdding(false);
  }

  async function makeCompanyWide(v: CompanyVendor): Promise<void> {
    if (!window.confirm(`Move ${v.name} to Company vendors? Its notes stay with ${LOCATION_NAMES[locationCode as LocationCode] ?? locationCode}.`)) return;
    await promoteCompanyVendor(v.id);
    await refetchResource(COMPANY_VENDORS_KEY, fetchCompanyVendors);
  }

  async function moveToClinic(v: CompanyVendor, code: string): Promise<void> {
    if (!window.confirm(`Move ${v.name} to ${LOCATION_NAMES[code as LocationCode] ?? code}? Every other clinic loses its note on this vendor.`)) return;
    await demoteCompanyVendor(v.id, code);
    await refetchResource(COMPANY_VENDORS_KEY, fetchCompanyVendors);
  }

  return (
    <section class="card" data-testid={`vendor-table-${scope}-${locationCode ?? 'company'}`}>
      <div class="section-head">
        <div class="section-head-row">
          <h2>{title}</h2>
          <div class="section-head-actions">
            {pin ?? null}
            {adding ? (
              <>
                <button type="button" class="btn" disabled={busy} onClick={() => setAdding(false)}>Cancel</button>
                <button type="button" class="btn primary" disabled={busy || !form.name.trim()} onClick={() => void run(save)}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : canWrite ? (
              <button type="button" class="btn" data-testid={`vendor-add-${scope}-${locationCode ?? 'company'}`} onClick={() => setAdding(true)}>
                Add vendor
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {error ? <small class="bad" role="alert">{error}</small> : null}
      {adding ? (
        <table>
          <tbody>
            {fields.map(([key, label]) => (
              <tr key={key}>
                <th scope="row">{label}</th>
                <td>
                  {key === 'notes' ? (
                    <textarea aria-label={label} value={form[key]} onInput={(e) => setForm({ ...form, [key]: (e.target as HTMLTextAreaElement).value })} />
                  ) : key === 'category' ? (
                    <>
                      <input
                        aria-label={label}
                        list={categoryListId}
                        value={form[key]}
                        onInput={(e) => setForm({ ...form, [key]: (e.target as HTMLInputElement).value })}
                      />
                      <datalist id={categoryListId}>
                        {categories.map((c) => <option key={c} value={c} />)}
                      </datalist>
                    </>
                  ) : (
                    <input aria-label={label} value={form[key]} onInput={(e) => setForm({ ...form, [key]: (e.target as HTMLInputElement).value })} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {rows.length === 0 && !adding ? (
        <p class="empty">No vendors in this list yet.</p>
      ) : (
        <div class="table-wrap">
          <table class="roster-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Website</th>
                <th>Notes</th>
                <th>Files</th>
                {canManage ? <th>Action</th> : null}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((v) => {
                const clinicNote = scope === 'COMPANY' && myClinic ? v.clinicNotes?.[myClinic] ?? null : null;
                return (
                  <tr
                    key={v.id}
                    class="vendor-row"
                    data-testid={`vendor-row-${v.id}`}
                    onClick={() => onOpen(v.id)}
                  >
                    <td>{v.name}</td>
                    <td>{v.category || EM_DASH}</td>
                    <td>{v.contactName || EM_DASH}</td>
                    <td>{v.phone || EM_DASH}</td>
                    <td>{v.email || EM_DASH}</td>
                    <td>{v.website || EM_DASH}</td>
                    <td>{scope === 'COMPANY' ? (clinicNote || EM_DASH) : (v.notes || EM_DASH)}</td>
                    <td>{v.fileCount ?? 0}</td>
                    {canManage ? (
                      <td onClick={(e) => e.stopPropagation()}>
                        {scope === 'LOCATION' ? (
                          <button
                            type="button"
                            class="btn"
                            data-testid={`vendor-promote-${v.id}`}
                            disabled={busy}
                            onClick={() => void run(() => makeCompanyWide(v))}
                          >
                            Make company-wide
                          </button>
                        ) : (
                          <select
                            aria-label={`Move ${v.name} to a clinic`}
                            data-testid={`vendor-demote-${v.id}`}
                            disabled={busy}
                            value=""
                            onChange={(e) => {
                              const code = (e.target as HTMLSelectElement).value;
                              (e.target as HTMLSelectElement).value = '';
                              if (code) void run(() => moveToClinic(v, code));
                            }}
                          >
                            <option value="">Move to clinic…</option>
                            {LOCATION_ORDER.map((code) => (
                              <option key={code} value={code}>{LOCATION_NAMES[code]}</option>
                            ))}
                          </select>
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function VendorCard(
  { vendorId, onBack, myClinic, categories }:
  { vendorId: number; onBack: () => void; myClinic: LocationCode | null; categories: string[] },
): JSX.Element {
  const load = useResource<{ vendor: CompanyVendor; files: CompanyVendorFile[] }>(
    vendorProfileKey(vendorId),
    () => fetchCompanyVendor(vendorId),
  );
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkName, setLinkName] = useState('');
  const { busy, error, run } = useWrite();
  const canWrite = canWriteVendors();

  if (load.status === 'loading') {
    return (
      <div class="main">
        <button type="button" class="link" onClick={onBack}>Back to Vendors</button>
        <EmptyState label="Loading vendor…" />
      </div>
    );
  }
  if (load.status === 'fault') {
    return (
      <div>
        <button type="button" class="link" onClick={onBack}>Back to Vendors</button>
        <FaultState name="vendor_load_failed" detail={load.error} />
      </div>
    );
  }

  const { vendor, files } = load.data;
  // A company-wide vendor's details/files are the owner's to change; the
  // clinic note below stays writable by the clinic (canWrite).
  const canEditVendor = vendor.scope === 'COMPANY' ? canWriteCompanyWide() : canWrite;
  const where = vendor.scope === 'COMPANY'
    ? 'Company'
    : (LOCATION_NAMES[vendor.locationCode as LocationCode] ?? vendor.locationCode);

  async function reload(): Promise<void> {
    await refetchResource(vendorProfileKey(vendorId), () => fetchCompanyVendor(vendorId));
    await refetchResource(COMPANY_VENDORS_KEY, fetchCompanyVendors);
  }

  async function save(): Promise<void> {
    await saveCompanyVendor({
      scope: vendor.scope,
      locationCode: vendor.locationCode,
      ...form,
    }, vendor.id);
    await reload();
    setEditing(false);
  }

  async function remove(): Promise<void> {
    await removeCompanyVendor(vendor.id);
    await refetchResource(COMPANY_VENDORS_KEY, fetchCompanyVendors);
    onBack();
  }

  async function attachLink(): Promise<void> {
    await addVendorLink(vendor.id, linkUrl, linkName);
    setLinkUrl('');
    setLinkName('');
    await reload();
  }

  async function pickFile(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    await uploadVendorFile(vendor.id, file);
    input.value = '';
    await reload();
  }

  return (
    <div class="person-profile" data-testid="vendor-card">
      <div class="page-header">
        <button type="button" class="link" data-testid="vendor-back" onClick={onBack}>
          Back to Vendors
        </button>
      </div>
      <section class="card">
        <div class="section-head">
          <div class="section-head-row">
            <h2>{vendor.name}</h2>
            <div class="section-head-actions">
              {editing ? (
                <>
                  <button type="button" class="btn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
                  <button type="button" class="btn primary" disabled={busy || !form.name.trim()} onClick={() => void run(save)}>
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : canEditVendor ? (
                <>
                  <button type="button" class="btn" data-testid="vendor-edit" onClick={() => { setForm(fromVendor(vendor)); setEditing(true); }}>
                    Edit details
                  </button>
                  <button type="button" class="btn" data-testid="vendor-delete" disabled={busy} onClick={() => void run(remove)}>
                    Delete
                  </button>
                </>
              ) : null}
            </div>
          </div>
          <p>{where} vendor</p>
        </div>
        {error ? <small class="bad" role="alert">{error}</small> : null}
        <table>
          <tbody>
            {fieldsForScope(vendor.scope).map(([key, label]) => (
              <tr key={key}>
                <th scope="row">{label}</th>
                <td>
                  {editing ? (
                    key === 'notes' ? (
                      <textarea aria-label={label} value={form[key]} onInput={(e) => setForm({ ...form, [key]: (e.target as HTMLTextAreaElement).value })} />
                    ) : key === 'category' ? (
                      <>
                        <input
                          aria-label={label}
                          list="vendor-categories-card"
                          value={form[key]}
                          onInput={(e) => setForm({ ...form, [key]: (e.target as HTMLInputElement).value })}
                        />
                        <datalist id="vendor-categories-card">
                          {categories.map((c) => <option key={c} value={c} />)}
                        </datalist>
                      </>
                    ) : (
                      <input aria-label={label} value={form[key]} onInput={(e) => setForm({ ...form, [key]: (e.target as HTMLInputElement).value })} />
                    )
                  ) : key === 'website' && vendor.website ? (
                    <a href={vendor.website} target="_blank" rel="noreferrer">{vendor.website}</a>
                  ) : key === 'email' && vendor.email ? (
                    <a href={`mailto:${vendor.email}`}>{vendor.email}</a>
                  ) : (
                    (vendor[key] as string | null) || EM_DASH
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {vendor.scope === 'COMPANY' ? (
        <ClinicNoteSection
          vendorId={vendor.id}
          myClinic={myClinic}
          notes={myClinic ? vendor.clinicNotes?.[myClinic] ?? null : null}
          canWrite={canWrite}
          onSaved={reload}
        />
      ) : null}
      <section class="card" data-testid="vendor-files">
        <div class="section-head">
          <div class="section-head-row">
            <h2>Documents</h2>
            <div class="section-head-actions">
              {canEditVendor ? (
                <label class="btn">
                  Add a document
                  <input type="file" class="visually-hidden" aria-label="Add a document" disabled={busy} onChange={(e) => void run(() => pickFile(e))} />
                </label>
              ) : null}
            </div>
          </div>
        </div>
        {canEditVendor ? (
          <div class="mgmt-add">
            <input aria-label="Document link" placeholder="https://…" value={linkUrl} onInput={(e) => setLinkUrl((e.target as HTMLInputElement).value)} />
            <input aria-label="Document name" placeholder="Name" value={linkName} onInput={(e) => setLinkName((e.target as HTMLInputElement).value)} />
            <button type="button" class="btn" disabled={busy || !linkUrl.trim()} onClick={() => void run(attachLink)}>
              Add link
            </button>
          </div>
        ) : null}
        {files.length === 0 ? (
          <p class="empty">No documents on this vendor yet.</p>
        ) : (
          <ul class="nhp-pack">
            {files.map((f) => (
              <li key={f.id} class="nhp-pack-row" data-testid={`vendor-file-${f.id}`}>
                <a href={f.url} target="_blank" rel="noreferrer">{f.name || f.url}</a>
                {canEditVendor ? (
                  <button type="button" class="btn" disabled={busy} onClick={() => void run(async () => { await removeVendorFile(f.id); await reload(); })}>
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * N-096: one clinic's own note on a company-wide vendor — never the shared
 * fields, and never another clinic's note. `myClinic` is the clinic
 * currently selected on the Company page (CompanyVendors passes
 * clinics[0]); with no clinic in context there is nothing to show or save.
 */
function ClinicNoteSection(
  { vendorId, myClinic, notes, canWrite, onSaved }:
  {
    vendorId: number;
    myClinic: LocationCode | null;
    notes: string | null;
    canWrite: boolean;
    onSaved: () => Promise<void>;
  },
): JSX.Element | null {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const { busy, error, run } = useWrite();

  if (!myClinic) return null;
  const clinicName = LOCATION_NAMES[myClinic] ?? myClinic;

  async function save(): Promise<void> {
    await saveVendorClinicNote(vendorId, myClinic as LocationCode, draft);
    await onSaved();
    setEditing(false);
  }

  return (
    <section class="card" data-testid="vendor-clinic-note">
      <div class="section-head">
        <div class="section-head-row">
          <h2>{`Notes for ${clinicName}`}</h2>
          <div class="section-head-actions">
            {editing ? (
              <>
                <button type="button" class="btn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
                <button type="button" class="btn primary" disabled={busy} onClick={() => void run(save)}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </>
            ) : canWrite ? (
              <button type="button" class="btn" data-testid="vendor-clinic-note-edit" onClick={() => { setDraft(notes ?? ''); setEditing(true); }}>
                Edit
              </button>
            ) : null}
          </div>
        </div>
      </div>
      {error ? <small class="bad" role="alert">{error}</small> : null}
      {editing ? (
        <textarea
          aria-label={`Notes for ${clinicName}`}
          value={draft}
          onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
        />
      ) : (
        <p>{notes || EM_DASH}</p>
      )}
    </section>
  );
}
